import { useState } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import BaseModal from "../shared/components/BaseModal";
import BaseButton from "components/common/BaseButton";
import { usAdminAppRoutes } from "../../../route-definitions";
import ErrorPage from "../shared/components/BaseError";
import EntityPageLayout from "../shared/components/EntityPageLayout";
import { useDistributor } from "./hooks/useDistributor";
import useMutateDistributor from "./hooks/useMutateDistributor";
import { DistributorFormData } from "./components/edit-distributor-form/edit-distributor.schema";
import DistributorsSkeleton from "./components/skeletons/DistributorsSkeleton";
import EditDistributorForm from "./components/edit-distributor-form/EditDistributorForm";

const EditDistributorPage = () => {
  const intl = useIntl();
  const history = useHistory();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const { entityId } = useParams<{
    entityId: string;
  }>();

  const {
    data: distributor,
    isLoading,
    isError,
    error,
    refetch,
  } = useDistributor({ distributorId: entityId });

  const {
    editDistributor,
    editDistributorLoading,
    editDistributorError,
    editDistributorSuccess,
  } = useMutateDistributor({
    id: entityId,
    onEditSuccess: () => {
      setModalIsOpen(true);
    },
    onEditError() {
      setModalIsOpen(true);
    },
  });

  const onSubmit = (data: DistributorFormData) => {
    editDistributor({ ...data, distributorId: entityId });
  };

  if (isError)
    return (
      <EntityPageLayout headerMessage="usAdmin.editDistributor">
        <ErrorPage errorMessage={error} onRetry={refetch} />
      </EntityPageLayout>
    );

  if (isLoading)
    return (
      <EntityPageLayout headerMessage="usAdmin.editDistributor">
        <DistributorsSkeleton />
      </EntityPageLayout>
    );

  const handleCloseModal = () => {
    setModalIsOpen(false);
    if (editDistributorSuccess) {
      history.push(usAdminAppRoutes.distributors);
    }
  };

  const distributorError = Array.isArray(
    editDistributorError?.response?.data?.message
  )
    ? editDistributorError.response.data.message[0]
    : editDistributorError?.response?.data?.message;

  return (
    <EntityPageLayout headerMessage="usAdmin.editDistributor">
      <BaseModal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        footer={
          <BaseButton
            handleSubmit={handleCloseModal}
            text={intl.formatMessage({ id: "usAdmin.ok" })}
          />
        }
      >
        {editDistributorSuccess && (
          <h2>
            {intl.formatMessage({
              id: "usAdmin.distributorUpdatedSuccessfully",
            })}
          </h2>
        )}
        {editDistributorError && <h2>{distributorError}</h2>}
      </BaseModal>
      <EditDistributorForm
        distributor={distributor!}
        onSubmit={onSubmit}
        isSubmitting={editDistributorLoading}
      />
    </EntityPageLayout>
  );
};

export default EditDistributorPage;
